
import { Vue, Component } from 'vue-property-decorator';

import {
  BaseResponse,
  ReserveCoinInformations,
  ResponseType,
  ValidateCoinName
} from '@/models/coin-setup';
import {
  BUSINESS_ONBOARDING_STORAGE_KEY,
  BUSINESS_ONBOARDING_UUID_CREATION,
  setBusinessOnboardingDataOnLocalStorage
} from '@/store/modules/businessOnboarding';

import CoinSetupService from '@/api/CoinSetupService';
import ImageUploadService from '@/api/ImageUploadService';
import SignUpService from '@/api/SingupService';
import BusinessService from '@/api/BusinessService';

import recaptchaRoutesKeys from '@/constants/recaptchaRoutesKeys';
import flagKeys from '@/constants/flagKeys';

import uuidv4 from '@/utils/uuidv4';
import {
  baseCoinImageConfig,
  getBlobFromUrl,
  preloadWithPromise,
  svgElementToImg
} from '@/utils/image';

import {
  BusinessOnboardingState,
  BusinessOnboardingYourDigitalCoinsForm
} from '@/models/business-onboarding';
import { OnlineOnboardingURL } from '@/views/Onboarding/helpers';
import eventBus from '@/utils/eventBus';
import HTTPStatus from '@/constants/httpStatus';
import { isExternalOauthSessionOnline } from '@/router/router-utils';

@Component({})
export default class YourDigitalCoinBase extends Vue {
  coinSetupService = new CoinSetupService();
  imageUploadService = new ImageUploadService();
  signUpService = new SignUpService();
  businessService = new BusinessService();

  loading = false;
  nameUnavaiable = false;
  uploadFailed = false;
  hasGeneralError = false;
  initialValuesForm: BusinessOnboardingYourDigitalCoinsForm = {
    coinShowFront: true,
    coinName: '',
    color: '',
    colorKey: '',
    isLoadingImg: false
  };
  coinSelectIds = {
    front: 'coin-mirror-svg-element-front',
    back: 'coin-mirror-svg-element-back'
  };
  businessId = '';
  shouldAddBusinessId = false;

  get businessUUID() {
    return localStorage.getItem(BUSINESS_ONBOARDING_UUID_CREATION) || uuidv4();
  }

  get flags() {
    return this.$store.getters['flags/getFlags'] || [];
  }

  get coinImageIsOptional() {
    return this.flags.includes(flagKeys.npoCoinImageOptional);
  }

  get tPrefix() {
    return 'common.components.interactiveCoin';
  }

  get stateBusinessOnboarding() {
    return this.$store.getters[
      'businessOnboarding/getBusinessOnboardingState'
    ] as BusinessOnboardingState;
  }

  get isLogged() {
    const token = this.signUpService.getCredentials();

    return !!token || this.$store.getters['manager/isImpersonated'];
  }

  get businessInfo() {
    return this.$store.getters['business/getBusinessInfo'].business;
  }

  shouldSentRecaptcha(featureFlag: string) {
    return this.flags.includes(featureFlag);
  }

  handleNextStep() {
    this.$router.push(OnlineOnboardingURL.LOYALTY_WIDGET);
  }

  async getRecaptcha(route: string, featureFlag: string) {
    let recaptcha = '';

    if (this.shouldSentRecaptcha(featureFlag)) {
      recaptcha = await this.$recaptcha(route);
    }

    return recaptcha;
  }

  async uploadImage({
    svgElementId,
    type
  }: {
    svgElementId: string;
    type: ResponseType;
  }): Promise<BaseResponse<any>> {
    const svgElement = document.getElementById(svgElementId)! as HTMLElement &
      SVGElement;

    const formData = new FormData();

    const { blob } = await svgElementToImg({
      ...baseCoinImageConfig,
      svgElement: svgElement
    });

    formData.append('data', blob);

    try {
      const recaptcha = await this.getRecaptcha(
        recaptchaRoutesKeys.businessCoinSetupUpload,
        flagKeys.businessCoinSetupUploadCaptcha
      );

      const { data } = await this.imageUploadService.uploadCoinImage(
        formData,
        recaptcha
      );

      if(data && type === ResponseType.COIN_FRONT_UPLOAD) {
        window.parent.postMessage(
          {
            type: 'branded-coin:updated',
            data: data
          },
          '*'
        );
      }

      return {
        success: !!data,
        type,
        data: data || null
      };
    } catch (err) {
      this.hasGeneralError = true;
      return {
        success: false,
        type,
        data: null
      };
    }
  }

  async uploadImages(data: BusinessOnboardingYourDigitalCoinsForm) {
    let types = ['back'];

    if (data.image && (data.image.imageFile || data.image.imageUrl)) {
      types = [...types, 'front', 'original'];
    }

    const methods: Record<string, () => Promise<BaseResponse<any>>> = {
      front: async () => {
        return await this.uploadImage({
          svgElementId: this.coinSelectIds.front,
          type: ResponseType.COIN_FRONT_UPLOAD
        });
      },
      back: async () => {
        return await this.uploadImage({
          svgElementId: this.coinSelectIds.back,
          type: ResponseType.COIN_BACK_UPLOAD
        });
      },
      original: async () => {
        try {
          const recaptcha = await this.getRecaptcha(
            recaptchaRoutesKeys.businessCoinSetupUpload,
            flagKeys.businessCoinSetupUploadCaptcha
          );

          const formData = new FormData();

          if (data.image && (data.image.imageFile || data.image.imageUrl)) {
            const blob =
              data.image.imageFile instanceof Blob
                ? data.image.imageFile
                : await getBlobFromUrl(data.image.imageUrl);

            formData.append('data', blob);
          }

          const response = await this.imageUploadService.uploadCoinImage(
            formData,
            recaptcha
          );

          return {
            success: !!response.data,
            type: ResponseType.ORIGINAL_LOGO_UPLOAD,
            data: response.data
          };
        } catch (err) {
          this.hasGeneralError = true;
          return {
            success: false,
            type: ResponseType.ORIGINAL_LOGO_UPLOAD,
            data: null
          };
        }
      }
    };

    const filteredServiceMethods = Object.entries(methods).filter(([key]) =>
      types.includes(key)
    );

    const services = Object.values(
      Object.fromEntries(filteredServiceMethods)
    ).map(method => method());

    return Promise.all(services);
  }

  async checkCoinAvailability(
    payload: ValidateCoinName
  ): Promise<BaseResponse<any>> {
    const transformedPayload = Object.assign({}, payload, {
      name: '$' + payload.name
    });

    const recaptcha = await this.getRecaptcha(
      recaptchaRoutesKeys.businessCoinSetupValidate,
      flagKeys.businessCoinSetupReserveCaptcha
    );

    if (this.shouldAddBusinessId) {
      transformedPayload.businessId = this.businessId || this.businessInfo.id;
    }

    try {
      const { data } = await this.coinSetupService.validateCoinName(
        transformedPayload,
        recaptcha
      );

      return {
        success: data.valid,
        type: ResponseType.COIN_NAME,
        data: null
      };
    } catch (err) {
      this.hasGeneralError = true;
      return {
        success: false,
        type: ResponseType.COIN_NAME,
        data: null
      };
    }
  }

  errorHandler(responses: Array<BaseResponse<any>>) {
    const filteredErrorResponses = responses.filter(({ success }) => !success);

    const handler = (type: ResponseType) => {
      switch (type) {
        case ResponseType.COIN_BACK_UPLOAD:
        case ResponseType.COIN_FRONT_UPLOAD:
        case ResponseType.ORIGINAL_LOGO_UPLOAD: {
          if (type !== ResponseType.COIN_BACK_UPLOAD) {
            this.uploadFailed = true;
          }

          break;
        }
        case ResponseType.COIN_NAME: {
          this.nameUnavaiable = true;

          const errorMessage = String(
            this.$t(`${this.tPrefix}.errorNameUnavailable`)
          );

          this.$notification(errorMessage, 'error');
          break;
        }
        default:
          break;
      }
    };

    for (const response of filteredErrorResponses) {
      handler(response.type);
    }
  }

  setFormDataOnStore(formData: BusinessOnboardingYourDigitalCoinsForm) {
    const newBusinessStoreState: BusinessOnboardingState = {
      ...this.stateBusinessOnboarding,
      yourDigitalCoin: {
        form: formData
      }
    };

    this.$store.dispatch(
      'businessOnboarding/SET_YOUR_DIGITAL_COIN_FORM',
      formData
    );
    setBusinessOnboardingDataOnLocalStorage(newBusinessStoreState);
  }

  async handleUpdateBusiness() {
    const { status, data } = await this.businessService.getBusinessInfo();

    if (status !== HTTPStatus.OK) return;

    eventBus.$emit('setBusinessInfo', data);

    this.$store.dispatch('business/SET_BUSINESS_INFO', {
      ...data,
      manager: data.loggedInManager
    });
  }

  async handleReserveCoinInformations(
    payload: ReserveCoinInformations,
    formData: BusinessOnboardingYourDigitalCoinsForm,
    images?: Record<string, any>
  ) {
    const recaptchaCoinSetupReserve = await this.getRecaptcha(
      recaptchaRoutesKeys.businessCoinSetupReserve,
      flagKeys.businessCoinSetupReserveCaptcha
    );

    const {
      status,
      data
    } = await this.coinSetupService.reserveCoinInformations(
      payload,
      recaptchaCoinSetupReserve
    );

    if (status !== 201) {
      this.$notification(
        String(this.$t(`${this.tPrefix}.errorReserveInfos`)),
        'error'
      );

      return;
    }

    const frontImageUrl = data.coinFront2d ? data.coinFront2d.large : '';

    const hasImageUrlAndOptionalCoinImageIsActive =
      frontImageUrl && this.coinImageIsOptional;

    if (hasImageUrlAndOptionalCoinImageIsActive) {
      await preloadWithPromise(frontImageUrl);
    }

    const newFormData = Object.assign(
      {},
      formData,
      hasImageUrlAndOptionalCoinImageIsActive
        ? {
            coinFrontImgUrlSaved: frontImageUrl
          }
        : {}
    );

    if (isExternalOauthSessionOnline() && this.businessInfo) {
      await this.coinSetupService.identifyBusiness({
        uid: this.businessUUID,
        businessId: this.businessId || this.businessInfo.id
      });

      if (localStorage.getItem(BUSINESS_ONBOARDING_STORAGE_KEY)) {
        this.handleUpdateBusiness();
      }
    }

    this.nameUnavaiable = false;
    Object.assign(this.initialValuesForm, newFormData);
    this.setFormDataOnStore(newFormData);
    this.handleNextStep();
  }

  async successHandler(
    formData: BusinessOnboardingYourDigitalCoinsForm,
    responses: Array<BaseResponse<any>>
  ) {
    const payload = Object.assign({}, formData);

    const findResponse = (type: ResponseType) => {
      return responses.find(res => res.type === type);
    };

    const backImageResponse = findResponse(ResponseType.COIN_BACK_UPLOAD);
    const frontImageResponse = findResponse(ResponseType.COIN_FRONT_UPLOAD);
    const originalLogoImageResponse = findResponse(
      ResponseType.ORIGINAL_LOGO_UPLOAD
    );

    if (payload.image && originalLogoImageResponse) {
      const originalLogoImageUrl =
        originalLogoImageResponse.data.publicPath.original;

      payload.image.imageUrl = originalLogoImageUrl;
    }

    if (!this.coinImageIsOptional) {
      const frontImageUrl =
        frontImageResponse && frontImageResponse.data.publicPath.large;

      await preloadWithPromise(frontImageUrl);
      payload.coinFrontImgUrlSaved = frontImageUrl;
    }

    const tranformedPayload: any = {
      uid: this.businessUUID,
      color: payload.color.replace('#', ''),
      colorLabel: payload.colorKey,
      finish: true,
      ...(backImageResponse && {
        coinBackImageId: backImageResponse.data.id
      }),
      ...(frontImageResponse && {
        coinFrontImageId: frontImageResponse.data.id
      }),
      ...(originalLogoImageResponse && {
        logoImageId: originalLogoImageResponse.data.id
      }),
      ...(this.shouldAddBusinessId &&
        (this.businessInfo || this.businessId) && {
          businessId: this.businessId || this.businessInfo.id
        })
    };

    if (payload.coinName) {
      tranformedPayload.coinName = '$' + payload.coinName;
    }

    await this.handleReserveCoinInformations(tranformedPayload, payload, {
      backImageResponse,
      frontImageResponse,
      originalLogoImageResponse
    });
  }

  async handleSubmit(formData: BusinessOnboardingYourDigitalCoinsForm) {
    const { coinName } = formData;

    this.loading = true;
    this.$showLoader();

    let requests: any = [this.uploadImages(formData)];
    if (coinName) {
      requests = [
        this.checkCoinAvailability({
          name: coinName,
          uid: this.businessUUID
        }),
        ...requests
      ];
    }
    const responses = await Promise.all(requests);

    if (this.hasGeneralError) {
      const errorMessage = String(this.$t(`${this.tPrefix}.errorGeneral`));
      this.$notification(errorMessage, 'error');

      this.loading = false;
      this.$hideLoader();

      return;
    }

    const flattenedResponses = responses.flat();
    const hasErrors = flattenedResponses.some(({ success }) => !success);

    if (hasErrors) {
      this.errorHandler(flattenedResponses);

      this.loading = false;
      this.$hideLoader();

      return;
    }

    await this.successHandler(formData, flattenedResponses);

    this.loading = false;
    this.$hideLoader();
  }
}
